import { z } from 'zod';
/**
 * **How long a service takes for one particular guest** (spec §4.6, feature 5;
 * `docs/lovespa-discovery.md` §2.1).
 *
 * The owner said it in one breath: *"Your eyebrow wax takes me 15 minutes, so I
 * only need to book 45 minutes 'cause I do a half an hour between clients…
 * Susie's takes me 35 minutes… facials and massages are always the same. It's
 * only waxing that the time changes."* A catalog duration is the house's
 * general answer; this is the owner's specific one, about a named person, and
 * it is the number the phone agent was promised would know that *Maria's brows
 * take thirty*.
 *
 * This file is the **vocabulary only** — the bounds, and the shape a remembered
 * length takes on any wire that ever carries one. Three other places implement
 * against it and none of them re-states the numbers:
 *
 *   * `client_service_durations` (migration 0036) carries them as a CHECK, which
 *     is the floor under every writer including a psql session;
 *   * `packages/store`'s `setClientDuration` refuses out of range before the
 *     statement is sent, so a staff surface gets a sentence rather than a
 *     constraint name;
 *   * `effectiveDurationMin` in `@yourstore/scheduling` is the one resolver that
 *     decides when the number is used at all.
 *
 * `domain` depends on nothing, which is why the constants live here: the staff
 * PWA validates the field the owner types into, the store validates the row, and
 * a browser bundle importing a database driver to learn that five is the minimum
 * would be the reason this package has the rule it has.
 */
/**
 * Five minutes. Below this is a typo rather than an appointment — and a span
 * shorter than the grid step would be offered and then never land on it.
 */
export const CLIENT_DURATION_MIN_MINUTES = 5;
/**
 * Eight hours. Deliberately **not** `24 * 60` (what `TurnRung` allows): a turn
 * time describes a table that may legitimately be held for a whole service, and
 * this describes *one person's appointment*. A remembered length longer than a
 * working day is a fat finger on a phone keyboard, and the day it is genuinely
 * needed is the day somebody widens this constant on purpose.
 */
export const CLIENT_DURATION_MAX_MINUTES = 480;
/** Minutes, bounded. The same rule the CHECK and the repository enforce. */
export const ClientDurationMinutes = z
    .number()
    .int()
    .min(CLIENT_DURATION_MIN_MINUTES)
    .max(CLIENT_DURATION_MAX_MINUTES);
/**
 * One remembered length, as any surface reads it back.
 *
 * `service_id` and not `item_id`, because that is what this fact is called
 * everywhere a caller can see it — the catalog id the agreement's `/services`
 * hands out. The store's own column is `item_id`, since that is the table it
 * points at; the projection renames it once, here, rather than every consumer
 * learning both names.
 */
export const ClientServiceDuration = z.object({
    service_id: z.string().min(1).max(128),
    duration_min: ClientDurationMinutes,
});
//# sourceMappingURL=client-durations.js.map